import Segmented from "@/components/Segmented";
import { Fragment, useEffect, useMemo, useState } from "react";
import { http, registrarDonos, donoDe } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EscopoPauta, { useEscopo } from "@/components/EscopoPauta";
import { comDe } from "@/lib/escritorioInfo";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarClock, Check, Repeat, ArrowDownUp, SlidersHorizontal, Search, X } from "lucide-react";
import { toast } from "sonner";
import NewEventDialog, { EventTypeBadge } from "@/components/NewEventDialog";
import ExportMenu from "@/components/ExportMenu";
import { BadgeParticular, BadgeCompartilhado } from "@/components/Particular";
import AlertCards from "@/components/AlertCards";
import CompromissoCard, { AcaoAbrir, SOURCE_LABELS, destinoItem } from "@/components/CompromissoCard";
import { formatDate, formatParties, monthLabel, publicationDate, toTitleCasePt } from "@/lib/format";
import SugestaoInput from "@/components/SugestaoInput";
import { filtrarBuscaMes, montarSecoes, opcoesMes, rotuloDia, somaDias } from "@/lib/agendaFiltro";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import LastUpdatedLabel from "@/components/LastUpdatedLabel";
import { confirmBaixa } from "@/components/ConfirmBaixa";
import AutosTag from "@/components/AutosTag";
import MarcarFeito from "@/components/MarcarFeito";
import { SUB_FILTROS, subFiltroKey } from "@/lib/compromisso";

import ProcessoLink from "@/components/ProcessoLink";
const TABS = [
  { key: "all", label: "Todos", params: { status: "approved" } },
  { key: "audiencias", label: "Audiências", params: { status: "approved", event_type: "audiencia" } },
  { key: "prazos", label: "Prazos", params: { status: "approved", event_type: "prazo" } },
  { key: "compromissos", label: "Compromissos", params: { status: "approved", event_type: "reuniao" } },
  { key: "not_validated", label: "Não validados", params: { filter: "to_validate" } },
  { key: "cancelled", label: "Cancelados", params: { status: "cancelled" } },
];

// Data local (não UTC) no formato ISO usado pelo backend — mesma convenção do Painel,
// pra separar o que já ocorreu (data passada) do que ainda vai acontecer.
const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const PHASE_LABELS = {
  inicial: "Inicial",
  instrucao: "Instrução",
  julgamento: "Julgamento",
  outro: "Outro",
};

// Sub-etiqueta embaixo do badge "Tipo", específica por tipo de evento: fase (audiência),
// tipo de prazo (texto livre) ou tipo do compromisso "Outro" (ex-assunto de reunião).
const typeSubLabel = (hearing) => {
  const h = hearing || {};
  const type = h.event_type || "audiencia";
  if (type === "audiencia") return h.hearing_phase ? (PHASE_LABELS[h.hearing_phase] || h.hearing_phase) : null;
  if (type === "prazo") return h.deadline_type || null;
  if (type === "reuniao") return null; // Lote 112: o subtipo agora é a própria etiqueta
  return null;
};

// Bloco 2: eventos ainda não validados (audiência/prazo detectado automaticamente pelo
// sistema, aguardando conferência do advogado) ganham uma etiqueta e uma cor distintas —
// azul claro — pra chamar atenção na Agenda sem depender de abrir "Para validar".
const NotValidatedBadge = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-blue-700 bg-blue-100 border border-blue-200 rounded px-1.5 py-0.5">
    Não validado
  </span>
);

// Lote 45f (pedido do Robson, 23/09/2026): prazo marcado como feito ganha o mesmo
// tratamento visual do "Não validado" (badge de cor própria), só que verde — não mexe
// no Google Calendar, só tira da lista "Próximos compromissos" do Painel e desce pra uma
// seção separada aqui na Agenda (ver PrazosFeitosSection mais abaixo).
const PrazoFeitoBadge = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
    <Check className="w-3 h-3" strokeWidth={2.5} /> Feito
  </span>
);

// Acompanhar agendamento de perícia (Lote novo, 29/09/2026): prazo marcado "repetir
// diariamente" ganha um selo próprio, pra ficar claro na lista que ele vai continuar
// avisando todo dia até ser marcado como feito (some junto com o próprio prazo quando o
// PrazoFeitoBadge aparece, já que não faz sentido mostrar os dois).
const RecorrenteBadge = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
    <Repeat className="w-3 h-3" strokeWidth={2.5} /> Repete diariamente
  </span>
);

// Lote 117 (Robson, 24/10/2026): audiências e compromissos também ganham o ✓ "Realizada"
// (só quando a data já chegou — hoje ou passada); prazos continuam com o ✓ a qualquer momento.
const ehPrazoItem = (h) => (h.hearing?.event_type || "audiencia") === "prazo";
const podeBaixarItem = (h) => !h.compartilhado && ehPrazoItem(h) || h.prazo_done === true || (!!h.hearing?.date && h.hearing.date <= todayISO());
const BaixaBadge = ({ h }) => (ehPrazoItem(h) ? <PrazoFeitoBadge /> : <RealizadaBadge />);
const RealizadaBadge = () => (
  <span data-testid="realizada-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
    <Check className="w-3 h-3" strokeWidth={2.5} /> Realizada
  </span>
);

// Segunda linha do compromisso (desktop): tipo do prazo / fase da audiência; na falta, a origem.
const apoioLinha = (h) => {
  const sub = typeSubLabel(h.hearing);
  return sub || (h.source ? (SOURCE_LABELS[h.source] || h.source) : "");
};

const HearingRow = ({ h, muted, onOpen, onToggleDone }) => {
  const notValidated = h.status === "approved" && h.validated !== true;
  const prazoDone = h.prazo_done === true;
  const x = h.hearing || {};
  const vara = toTitleCasePt((x.court || "").trim());
  const comarca = toTitleCasePt((x.court_district || "").trim());
  const apoio = apoioLinha(h);
  const temExtras = notValidated || x.autos_tag || prazoDone || x.recurring_daily;
  return (
    <TableRow
      onClick={() => onOpen(h)}
      data-testid={`row-hearing-${h.pub_id}`}
      className={`cursor-pointer ${muted ? "opacity-60" : ""} ${notValidated ? "bg-blue-100" : ""}`}
    >
      <TableCell className="whitespace-nowrap align-middle w-[1%]">
        <div className="flex items-center gap-3">
          <span className="font-display text-lg font-bold tabular-nums text-stone-900 min-w-[3.25rem]">{x.time || "—"}</span>
          <EventTypeBadge hearing={x} />
        </div>
      </TableCell>
      <TableCell className="align-middle max-w-[280px]">
        <div className="text-sm font-semibold text-stone-900 truncate">{formatParties(x.parties) || x.case_number || "—"}</div>
        {apoio && (
          <div className="text-xs text-stone-500 truncate" data-testid={`type-sublabel-${h.pub_id}`}>{apoio}</div>
        )}
        {h.responsavel_nome && <div className="text-xs text-stone-500 truncate" data-testid={`resp-${h.pub_id}`}>Resp.: {h.responsavel_nome}{h.tambem_em?.length > 0 ? ` · também na conta de ${h.tambem_em.map((t) => t.nome).join(", ")}` : ""}</div>}
        {(h.particular || h.particular_ef) && <div className="mt-1"><BadgeParticular testid={`particular-${h.pub_id}`} /></div>}
        {h.compartilhado && <div className="mt-1"><BadgeCompartilhado de={h.compartilhado.de_nome} testid={`compartilhado-${h.pub_id}`} /></div>}
        {temExtras && (
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            {notValidated && <NotValidatedBadge />}
            {x.autos_tag && <AutosTag autos={x.autos_tag.autos} nome={x.autos_tag.autos_nome} />}
            {prazoDone && <BaixaBadge h={h} />}
            {!prazoDone && x.recurring_daily && <RecorrenteBadge />}
          </div>
        )}
      </TableCell>
      <TableCell className="align-middle text-stone-700 max-w-[200px]">
        {vara || comarca ? (
          <>
            <div className="text-sm truncate">{vara || comarca}</div>
            {vara && comarca && <div className="text-sm text-stone-600 truncate">{comarca}</div>}
          </>
        ) : "—"}
      </TableCell>
      <TableCell className="align-middle tabular-nums">
        {x.case_number ? (h.compartilhado ? <span className="break-all">{x.case_number}</span> : <ProcessoLink numero={x.case_number} />) : "—"}
        {publicationDate(h) && x.event_type !== "reuniao" && (
          <div className="text-[11px] text-stone-400 font-normal" data-testid={`publicado-em-${h.pub_id}`}>Publicado em {formatDate(publicationDate(h))}</div>
        )}
      </TableCell>
      <TableCell className="align-middle w-[1%]">
        {/* Mesmo desenho do celular: Abrir em cima, Concluir embaixo, do mesmo tamanho. */}
        <div className="flex flex-col items-stretch gap-1.5 w-[7.25rem] ml-auto">
          <AcaoAbrir h={h} />
          {podeBaixarItem(h) && <MarcarFeito coluna h={h} onToggleDone={onToggleDone} />}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Faixa do dia ("SEGUNDA-FEIRA · 26 OUTUBRO — 5 compromissos"), com HOJE/AMANHÃ em destaque.
const DiaCabecalho = ({ iso, n, hoje, vencido = false, className = "" }) => {
  const marca = iso === hoje ? "Hoje" : iso === somaDias(hoje, 1) ? "Amanhã" : null;
  return (
    <div data-testid={`agenda-dia-${iso}`} className={`flex items-center justify-between gap-x-3 gap-y-1 px-4 py-2.5 bg-stone-50 border-y border-stone-200 ${className}`}>
      <div className="flex items-center gap-x-2 gap-y-1 flex-wrap min-w-0">
        <span className="text-[11px] font-bold tracking-[0.12em] sm:tracking-[0.18em] uppercase text-stone-800 whitespace-nowrap">{iso === "sem-data" ? "SEM DATA" : rotuloDia(iso)}</span>
        {marca && <span className="text-[10px] font-bold tracking-wider uppercase text-gold-800 bg-gold-500/15 rounded px-1.5 py-0.5">{marca}</span>}
        {vencido && <span data-testid={`agenda-vencido-${iso}`} className="text-[10px] font-bold tracking-wider uppercase text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Vencido</span>}
      </div>
      <span className="text-xs text-stone-500 whitespace-nowrap shrink-0">{n} {n === 1 ? "compromisso" : "compromissos"}</span>
    </div>
  );
};

// Lista da Agenda: seções (pendentes / realizadas / concluídos) → dias → compromissos.
// Computador: uma tabela só; celular: cartões. Os dois mostram a mesma separação por dia.
const AgendaLista = ({ secoes, hoje, muted, onToggleDone }) => {
  const navigate = useNavigate();
  const onOpen = (h) => navigate(destinoItem(h));
  const today = hoje;
  return (
    <>
      <div className="md:hidden" data-testid="hearings-cards">
        {secoes.map((sec) => (
          <div key={sec.id} data-testid={sec.rotulo ? `${sec.id === "ocorridos" ? "past-hearings-section" : "done-prazos-section"}-m` : undefined} className={sec.rotulo ? "border-t border-stone-200" : ""}>
            {sec.rotulo && <div className="px-4 pt-4 pb-1 text-[11px] font-bold tracking-widest uppercase text-stone-400 bg-stone-50">{sec.rotulo}</div>}
            {sec.grupos.map(([iso, itens]) => (
              <div key={iso}>
                <DiaCabecalho iso={iso} n={itens.length} hoje={hoje} vencido={sec.id === "pendentes" && iso !== "sem-data" && iso < hoje} />
                <div className="space-y-2.5 p-3">
                  {itens.map((h) => {
                    const notValidated = h.status === "approved" && h.validated !== true;
                    const prazoDone = h.prazo_done === true;
                    const silenciar = muted || sec.id !== "pendentes";
                    return (
                      <CompromissoCard
                        key={h.pub_id}
                        h={h}
                        isToday={h.hearing?.date === today && !silenciar}
                        muted={silenciar}
                        notValidated={notValidated}
                        extraBadges={
                          <>
                            {notValidated && <NotValidatedBadge />}
                            {h.hearing?.autos_tag && <AutosTag autos={h.hearing.autos_tag.autos} nome={h.hearing.autos_tag.autos_nome} />}
                            {prazoDone && <BaixaBadge h={h} />}
                            {!prazoDone && h.hearing?.recurring_daily && <RecorrenteBadge />}
                          </>
                        }
                        toggleNode={podeBaixarItem(h) ? <MarcarFeito coluna h={h} onToggleDone={onToggleDone} /> : null}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <Table className="hidden md:table">
        <TableHeader>
          <TableRow>
            <TableHead className="text-[11px] uppercase tracking-widest whitespace-nowrap">Horário</TableHead>
            <TableHead className="text-[11px] uppercase tracking-widest">Compromisso / Partes</TableHead>
            <TableHead className="text-[11px] uppercase tracking-widest">Vara / Comarca</TableHead>
            <TableHead className="text-[11px] uppercase tracking-widest">Processo</TableHead>
            <TableHead className="text-[11px] uppercase tracking-widest text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {secoes.map((sec) => (
            <Fragment key={sec.id}>
              {sec.rotulo && (
                <TableRow data-testid={sec.id === "ocorridos" ? "past-hearings-section" : "done-prazos-section"} className="hover:bg-stone-50 bg-stone-50">
                  <TableCell colSpan={5} className="px-4 pt-4 pb-1 text-[11px] font-bold tracking-widest uppercase text-stone-400">{sec.rotulo}</TableCell>
                </TableRow>
              )}
              {sec.grupos.map(([iso, itens]) => (
                <Fragment key={iso}>
                  <TableRow className="hover:bg-transparent border-0">
                    <TableCell colSpan={5} className="p-0"><DiaCabecalho iso={iso} n={itens.length} hoje={hoje} vencido={sec.id === "pendentes" && iso !== "sem-data" && iso < hoje} /></TableCell>
                  </TableRow>
                  {itens.map((h) => (
                    <HearingRow key={h.pub_id} h={h} muted={muted || sec.id !== "pendentes"} onOpen={onOpen} onToggleDone={onToggleDone} />
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

const lerLS = (k, pad, ok) => { try { const v = localStorage.getItem(k); return v && ok.includes(v) ? v : pad; } catch { return pad; } };
const gravarLS = (k, v) => { try { localStorage.setItem(k, v); } catch { /* sem armazenamento */ } };
const SITUACOES = [
  { key: "pendentes", label: "Situação: Pendentes" },
  { key: "concluidos", label: "Situação: Concluídos" },
  { key: "todos", label: "Situação: Todos" },
];
const capitalizar = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

export default function HearingsList() {
  const [rawItems, setItems] = useState([]);
  const [searchParams] = useSearchParams();
  // Lote 112: filtro por subtipo dentro de "Compromissos" (lembrado neste aparelho).
  const [sub, setSubState] = useState(() => { try { return localStorage.getItem("iapauta_agenda_sub") || "todos"; } catch { return "todos"; } });
  const setSub = (k) => { setSubState(k); try { localStorage.setItem("iapauta_agenda_sub", k); } catch { /* sem armazenamento */ } };
  // Lote 126: busca, situação (padrão Pendentes), mês e ordem — situação e ordem ficam lembradas neste aparelho.
  const [busca, setBusca] = useState("");
  const [mes, setMes] = useState("todos");
  const [situacao, setSituacaoState] = useState(() => lerLS("iapauta_agenda_situacao", "pendentes", SITUACOES.map((x) => x.key)));
  const setSituacao = (v) => { setSituacaoState(v); gravarLS("iapauta_agenda_situacao", v); };
  const [ordem, setOrdemState] = useState(() => lerLS("iapauta_agenda_ordem", "asc", ["asc", "desc"]));
  const setOrdem = (v) => { setOrdemState(v); gravarLS("iapauta_agenda_ordem", v); };
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const filter = searchParams.get("filter");
  const activeTab = TABS.some((t) => t.key === filter) ? filter : "all";
  const tab = TABS.find((t) => t.key === activeTab);
  const subAtivo = activeTab === "compromissos" && SUB_FILTROS.some((f) => f.key === sub) ? sub : "todos";
  const porSub = useMemo(
    () => (activeTab === "compromissos" && subAtivo !== "todos" ? rawItems.filter((h) => subFiltroKey(h.hearing) === subAtivo) : rawItems),
    [rawItems, activeTab, subAtivo],
  );
  const subCounts = useMemo(() => {
    const c = { todos: rawItems.length, reuniao: 0, pericia: 0, lembrete: 0, outros: 0 };
    rawItems.forEach((h) => { c[subFiltroKey(h.hearing)] += 1; });
    return c;
  }, [rawItems]);

  // Lote 157 — quem está num escritório vê "Minha pauta" (onde é o responsável) ou "Escritório" (de todos).
  const { user } = useAuth() || {};
  const noEscritorio = !!user?.escritorio;
  const [escopo, setEscopo] = useEscopo();
  const load = () => (noEscritorio
    ? http.get("/escritorio/agenda", { params: { ...tab.params, escopo: escopo.escopo, responsavel: escopo.responsavel || undefined } })
    : http.get("/publications", { params: tab.params })
  ).then(async (r) => {
    if (noEscritorio) registrarDonos(r.data, user?.user_id, "pub_id");
    // Lote 160 — eventos que parceiros compartilharam e que escolhi mostrar na minha agenda (somente leitura; falha aqui não derruba a lista).
    let extra = [];
    if (tab.params.status && tab.key !== "not_validated") {
      try {
        extra = (await http.get("/colab/agenda")).data.filter((c) => c.status === tab.params.status && (!tab.params.event_type || (c.hearing?.event_type || "audiencia") === tab.params.event_type));
      } catch { extra = []; }
    }
    setItems([...r.data, ...extra]);
  });
  useEffect(() => { load(); }, [activeTab, noEscritorio, escopo.escopo, escopo.responsavel]); // eslint-disable-line react-hooks/exhaustive-deps
  // Lote 57: atualiza sozinho ao voltar pra tela e a cada 15min enquanto fica aberta.
  const { lastUpdated } = useAutoRefresh(load);

  // Lote 45f: marcar/desmarcar prazo como feito — não mexe no Google Calendar, só some
  // do Painel e desce pra seção "Prazos concluídos" aqui embaixo (ver PrazoDoneToggle).
  const onToggleDone = async (h) => {
    const prazo = ehPrazoItem(h);
    const path = h.prazo_done ? "unmark-prazo-done" : "mark-prazo-done";
    if (!h.prazo_done && !(await confirmBaixa(h.hearing?.parties || h.hearing?.case_number || "", prazo ? "prazo" : "realizada"))) return;
    try {
      await http.post(`/publications/${h.pub_id}/${path}`);
      if (h.prazo_done) {
        toast.success(prazo ? "Prazo reaberto" : "Reaberta");
      } else if (prazo) {
        toast.success("Prazo marcado como feito");
      } else {
        toast.success("Marcada como realizada", {
          duration: 6000,
          action: { label: "Desfazer", onClick: () => { http.post(`/publications/${h.pub_id}/unmark-prazo-done`).then(load).catch(() => {}); } },
        });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const titleByTab = {
    all: "Todos os eventos agendados",
    audiencias: "Audiências",
    prazos: "Prazos",
    compromissos: "Compromissos",
    not_validated: "Eventos não validados",
    cancelled: "Eventos cancelados",
  };
  const emptyByTab = {
    all: "Nenhuma audiência aprovada ainda.",
    audiencias: "Nenhuma audiência agendada.",
    prazos: "Nenhum prazo agendado.",
    compromissos: "Nenhum compromisso agendado.",
    not_validated: "Nada pendente de validação — tudo em dia.",
    cancelled: "Nenhum evento cancelado.",
  };

  // Abas com eventos ativos (não cancelados / não "para validar") usam a Situação; as outras mostram tudo.
  const splitByDate = activeTab !== "cancelled" && activeTab !== "not_validated";
  const today = todayISO();
  const meses = useMemo(() => opcoesMes(porSub), [porSub]);
  const mesAtivo = mes !== "todos" && meses.includes(mes) ? mes : "todos";
  const filtrados = useMemo(() => filtrarBuscaMes(porSub, { busca, mes: mesAtivo }), [porSub, busca, mesAtivo]);
  const secoes = useMemo(
    () => montarSecoes(filtrados, { situacao: splitByDate ? situacao : "todos", ordem, hoje: today }),
    [filtrados, splitByDate, situacao, ordem, today],
  );
  // sem seção de "Realizadas/Concluídos" nas abas sem Situação: tudo numa lista só, por dia
  const secoesFinal = splitByDate ? secoes : secoes.map((sc) => ({ ...sc, rotulo: null }));
  const nFiltros = (splitByDate && situacao !== "pendentes" ? 1 : 0) + (mesAtivo !== "todos" ? 1 : 0) + (ordem !== "asc" ? 1 : 0);
  const limpar = () => { setBusca(""); setMes("todos"); setSituacao("pendentes"); setOrdem("asc"); };

  const selCls = "h-10 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-400";
  const controles = (
    <>
      {splitByDate && (
        <select data-testid="agenda-situacao" aria-label="Situação" value={situacao} onChange={(e) => setSituacao(e.target.value)} className={selCls}>
          {SITUACOES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      )}
      <select data-testid="agenda-mes" aria-label="Mês" value={mesAtivo} onChange={(e) => setMes(e.target.value)} className={selCls}>
        <option value="todos">Todos os meses</option>
        {meses.map((m) => <option key={m} value={m}>{capitalizar(monthLabel(m).replace(" de ", " "))}</option>)}
      </select>
      <button type="button" data-testid="agenda-ordem" onClick={() => setOrdem(ordem === "asc" ? "desc" : "asc")}
        aria-label={ordem === "asc" ? "Ordenar do mais recente para o mais antigo" : "Ordenar do mais antigo para o mais recente"}
        className="inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md border border-stone-200 bg-white text-sm text-stone-700 hover:border-stone-300 whitespace-nowrap">
        Data e hora <ArrowDownUp className="w-3.5 h-3.5 text-stone-500" strokeWidth={1.75} /> <span className="text-stone-500">{ordem === "asc" ? "↑" : "↓"}</span>
      </button>
    </>
  );

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2">
            Agenda
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-stone-900">
            {titleByTab[activeTab]}
          </h1>
        </div>
        <div className="flex flex-col md:items-end gap-2">
          <div className="flex items-center gap-2">
            <ExportMenu />
            <NewEventDialog onCreated={load} />
          </div>
          <LastUpdatedLabel date={lastUpdated} />
        </div>
      </header>

      {/* Lote 69: quadro de alertas (Ação agora / Próxima audiência / Conflito). Continua exatamente como era. */}
      <div className="mb-6">
        <AlertCards onChanged={load} />
      </div>

      {noEscritorio && <EscopoPauta valor={escopo} onChange={setEscopo} className="mb-3" />}

      <Segmented scroll value={activeTab} testid="hearings-tabs" className="mb-3"
        options={TABS.map((t) => ({ key: t.key, label: t.label, testid: `tab-${t.key}`, to: t.key === "all" ? "/audiencias" : `/audiencias?filter=${t.key}` }))} />

      {activeTab === "compromissos" && (
        <Segmented scroll size="sm" value={subAtivo} onChange={setSub} testid="compromisso-subfiltros" className="mb-3" ariaLabel="Tipo de compromisso"
          options={SUB_FILTROS.map((f) => ({ key: f.key, label: f.label, count: subCounts[f.key], testid: `subfiltro-${f.key}` }))} />
      )}

      {/* Lote 126: busca + situação + mês + ordem (no celular, situação/mês/ordem ficam atrás de "Filtros"). */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" strokeWidth={2} />
            <SugestaoInput
              type="text"
              testid="agenda-busca"
              fonte={["partes", "clientes", "varas", "comarcas"]}
              value={busca}
              onValueChange={setBusca}
              placeholder="Buscar parte ou processo"
              className="w-full pl-10 pr-9 h-10 text-sm rounded-md border border-stone-200 bg-white shadow-none focus-visible:ring-2 focus-visible:ring-stone-400"
            />
            {busca && (
              <button type="button" data-testid="agenda-busca-limpar" aria-label="Limpar busca" onClick={() => setBusca("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-stone-400 hover:text-stone-700">
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
          </div>
          <div className="hidden md:flex items-center gap-2 shrink-0">{controles}</div>
          <button type="button" data-testid="agenda-filtros-toggle" aria-expanded={filtrosAbertos} onClick={() => setFiltrosAbertos((v) => !v)}
            className="md:hidden relative inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md border border-stone-200 bg-white text-sm text-stone-700 shrink-0">
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.75} /> Filtros
            {nFiltros > 0 && <span className="min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-gold-500 text-black text-[10px] font-bold flex items-center justify-center">{nFiltros}</span>}
          </button>
        </div>
        {filtrosAbertos && (
          <div className="md:hidden mt-2 grid grid-cols-1 gap-2" data-testid="agenda-filtros-painel">{controles}</div>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {rawItems.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarClock className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
            <p className="text-stone-500">{emptyByTab[activeTab]}</p>
          </div>
        ) : secoesFinal.length === 0 ? (
          <div className="p-12 text-center" data-testid="agenda-vazio-filtro">
            <CalendarClock className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
            <p className="text-stone-500">
              {busca || mesAtivo !== "todos" || porSub.length === 0 ? "Nada encontrado com esses filtros." : "Nada pendente nesta aba."}
            </p>
            <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
              {splitByDate && situacao === "pendentes" && !busca && mesAtivo === "todos" && porSub.length > 0 && (
                <button type="button" data-testid="agenda-ver-concluidos" onClick={() => setSituacao("todos")}
                  className="h-9 px-3 rounded-md border border-stone-300 bg-white text-sm text-stone-800 hover:border-stone-400">Ver concluídos e já ocorridos</button>
              )}
              {(busca || mesAtivo !== "todos" || nFiltros > 0) && (
                <button type="button" data-testid="agenda-limpar-filtros" onClick={limpar}
                  className="h-9 px-3 rounded-md border border-stone-300 bg-white text-sm text-stone-800 hover:border-stone-400">Limpar filtros</button>
              )}
            </div>
          </div>
        ) : (
          <AgendaLista secoes={secoesFinal} hoje={today} muted={activeTab === "cancelled"} onToggleDone={onToggleDone} />
        )}
      </div>
    </div>
  );
}
